import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
  const { isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentToEmail, setSentToEmail] = useState('');

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isAuthenticated && user?.email) {
        const response = await authAPI.forgotPasswordMe();
        setSentToEmail(response.data.email || user.email);
      } else {
        const response = await authAPI.forgotPassword(email.trim());
        setSentToEmail(response.data.email || email.trim());
      }
      setEmailSent(true);
      toast.success('Şifre sıfırlama e-postası gönderildi');
    } catch (error) {
      const message = error.response?.data?.message || 'E-posta gönderilemedi';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoggedInSend = async () => {
    setLoading(true);
    try {
      const response = await authAPI.forgotPasswordMe();
      setSentToEmail(response.data.email || user.email);
      setEmailSent(true);
      toast.success('Kayıtlı e-posta adresinize gönderildi');
    } catch (error) {
      const message = error.response?.data?.message || 'E-posta gönderilemedi';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">E-posta gönderildi</h2>
              <p className="text-gray-600 mb-6">
                <strong>{sentToEmail}</strong> adresine (hesabınıza kayıtlı e-posta) şifre sıfırlama
                bağlantısı gönderildi.
              </p>
              <div className="space-y-3">
                <Link
                  to={isAuthenticated ? '/profile' : '/login'}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  {isAuthenticated ? 'Profile dön' : 'Giriş sayfasına dön'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && user?.email) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <Link to="/profile" className="flex items-center text-primary-600 hover:text-primary-500 mb-4 text-sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Profile dön
            </Link>
            <h2 className="text-2xl font-bold text-gray-900">Şifre sıfırlama</h2>
            <p className="mt-2 text-sm text-gray-600">
              Güvenlik için link yalnızca hesabınıza kayıtlı e-posta adresine gönderilir; başka adres
              girilemez.
            </p>
            <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-center gap-3">
              <Mail className="h-5 w-5 text-gray-400 shrink-0" />
              <span className="text-sm font-medium text-gray-900 break-all">{user.email}</span>
            </div>
            <button
              type="button"
              onClick={handleLoggedInSend}
              disabled={loading}
              className="mt-6 w-full flex justify-center py-2 px-4 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Gönderiliyor...' : 'Sıfırlama linkini gönder'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6">
            <Link to="/login" className="flex items-center text-primary-600 hover:text-primary-500 mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Giriş sayfasına dön
            </Link>
            <h2 className="text-2xl font-bold text-gray-900">Şifremi unuttum</h2>
            <p className="mt-2 text-sm text-gray-600">
              Kayıt olurken kullandığınız e-posta adresini girin. Link yalnızca hesabınızda kayıtlı
              adrese gider.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Kayıtlı e-posta adresi
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Kayıt olduğunuz e-posta"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Gönderiliyor...' : 'Şifre sıfırlama e-postası gönder'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
